import { useCallback, useRef, useState } from "react";
import JSZip from "jszip";
import { UploadCloud, Loader2, ShieldCheck, FileCheck2, FileX2,
  FileWarning, FileArchive, CheckCircle2, XCircle, RotateCcw, Lock, Link, AlertTriangle } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import LegalPage from "./LegalPage";

const IS_SUBSCRIBER = false; // unlocked with Professional & Enterprise accounts (launching soon)

// --- Path Normalizer ---
function normalizePath(p) {
  let clean = p.replace(/\\/g, "/").trim();
  const mediaIdx = clean.toLowerCase().indexOf("media/");
  if (mediaIdx !== -1) {
    return clean.substring(mediaIdx);
  }
  return clean;
}

async function sha256Hex(arrayBuffer) {
  const digest = await crypto.subtle.digest("SHA-256", arrayBuffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function parseHashes(text) {
  const expected = new Map();
  const originalPaths = new Map();
  let sourceHash = "";
  let generatedAt = "";
  
  const FORGIVING_HASH_LINE = /^([0-9a-fA-F]{64})\s+(.+)$/; 
  
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    const m = line.match(FORGIVING_HASH_LINE);
    if (m && m[2].toLowerCase().includes("media/")) {
      const norm = normalizePath(m[2]);
      expected.set(norm, m[1].toLowerCase());
      originalPaths.set(norm, m[2].trim());
      continue;
    }
    if (line.startsWith("Source SHA-256")) sourceHash = line.split(":").slice(1).join(":").trim();
    if (line.startsWith("Generated (UTC)")) generatedAt = line.split(":").slice(1).join(":").trim();
  }
  return { expected, originalPaths, sourceHash, generatedAt };
}

const STATUS = {
  ok: { icon: FileCheck2, cls: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Verified" },
  tampered: { icon: FileX2, cls: "text-red-600", chip: "bg-red-50 text-red-700 border-red-200", label: "Mismatch" },
  missing: { icon: FileWarning, cls: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Missing" },
  extra: { icon: FileWarning, cls: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Not in ledger" },
};

// Helper to strip any invisible spaces before comparing
const cleanHash = (h) => (h || "").replace(/[^a-fA-F0-9]/g, "").toLowerCase();

export default function VerifyBundle() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  
  // Independent States for the Dual-Slot Dashboard
  const [bundle, setBundle] = useState(null);
  const [source, setSource] = useState(null); 
  
  const bundleInputRef = useRef(null);
  const sourceInputRef = useRef(null);

  const reset = () => { 
    setBundle(null); 
    setSource(null); 
    setError(""); 
  };

  const processFiles = useCallback(async (fileList, targetSlot = "auto") => {
    setError(""); setBusy(true);
    try {
      let newBundle = bundle;
      let newSource = source;

      for (const file of Array.from(fileList)) {
        const buffer = await file.arrayBuffer();
        let isBundle = false;

        // Try to parse as AuditPDF Bundle
        if (file.name.toLowerCase().endsWith(".zip") && (targetSlot === "auto" || targetSlot === "bundle")) {
          try {
            const zip = await JSZip.loadAsync(buffer);
            const ledger = zip.file("hashes.txt");
            if (ledger) {
              isBundle = true;
              const text = await ledger.async("string");
              const { expected, originalPaths, sourceHash, generatedAt } = parseHashes(text);
              
              if (expected.size === 0) throw new Error("The integrity ledger contained no file hashes.");

              const actual = new Map();
              const mediaFiles = [];
              
              zip.forEach((path, entry) => {
                if (!entry.dir && path.toLowerCase().includes("media/")) {
                  mediaFiles.push([normalizePath(path), entry]);
                }
              });
              
              for (const [normPath, entry] of mediaFiles) {
                actual.set(normPath, await sha256Hex(await entry.async("arraybuffer")));
              }

              const rows = [];
              for (const [norm, expHash] of expected.entries()) {
                const displayPath = originalPaths.get(norm) || norm;
                if (!actual.has(norm)) {
                  rows.push({ rel: displayPath, status: "missing", expected: expHash, actual: "" });
                } else {
                  const actHash = actual.get(norm);
                  rows.push({ rel: displayPath, status: actHash === expHash ? "ok" : "tampered", expected: expHash, actual: actHash });
                }
              }
              for (const norm of actual.keys()) {
                if (!expected.has(norm)) rows.push({ rel: norm, status: "extra", expected: "", actual: actual.get(norm) });
              }
              rows.sort((a, b) => a.rel.localeCompare(b.rel));
              
              newBundle = { rows, sourceHash, generatedAt, fileName: file.name };
            }
          } catch (e) {
            if (e.message.includes("ledger")) throw e;
          }
        }

        // Try to parse as Source File
        if (!isBundle && (targetSlot === "auto" || targetSlot === "source")) {
          if (file.name.toLowerCase().endsWith(".zip")) {
             const rawZip = await JSZip.loadAsync(buffer);
             const txtFileName = Object.keys(rawZip.files).find(name => name.endsWith('.txt') && !name.includes('__MACOSX'));
             
             if (txtFileName) {
                const txtBuffer = await rawZip.file(txtFileName).async("arraybuffer");
                const hash = await sha256Hex(txtBuffer);
                newSource = { hash, fileName: txtFileName };
             } else {
                const hash = await sha256Hex(buffer);
                newSource = { hash, fileName: file.name };
             }
          } else {
             const hash = await sha256Hex(buffer);
             newSource = { hash, fileName: file.name };
          }
        }
      }

      if (newBundle) setBundle(newBundle);
      if (newSource) setSource(newSource);

    } catch (e) {
      setError(e?.message || "Could not process files. Please drop valid exports or bundles.");
    } finally {
      setBusy(false);
    }
  }, [bundle, source]);

  // Derived States
  const bundlePassed = bundle && bundle.rows.every((r) => r.status === "ok");
  const okCount = bundle ? bundle.rows.filter((r) => r.status === "ok").length : 0;
  
  // Safe comparison strips out potential invisible characters/casing issues
  const custodyMatch = bundle && source ? cleanHash(bundle.sourceHash) === cleanHash(source.hash) : null;

  // DYNAMIC UI LOGIC: Gray (Waiting), Green (Match), Red (Mismatch)
  const hashBoxStyle = bundle && source 
    ? (custodyMatch ? "bg-emerald-50 border-emerald-400 text-emerald-900" : "bg-red-50 border-red-400 text-red-900")
    : "bg-slate-50 border-slate-300 text-slate-800"; // <--- This keeps it Gray if only 1 file is uploaded

  if (!IS_SUBSCRIBER) {
    return (
      <LegalPage
        title="Verify Court Bundle | AuditPDF"
        description="Independently verify the integrity of an AuditPDF evidence bundle and its source chain of custody."
        canonical="https://auditpdfhq.com/verify">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center" data-testid="verify-paywall">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-900 text-white">
            <Lock size={26} />
          </span>
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-900">
            Bundle Verification is a subscriber feature
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-600">
            The independent chain-of-custody Verification Dashboard is included with the
            <strong> Professional</strong> and <strong>Enterprise</strong> plans.
          </p>
          <ul className="mx-auto mt-8 max-w-md space-y-3 text-left">
            <li className="flex items-start gap-3 text-sm text-slate-700">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
              <span>Mathematically confirm every media file in a bundle against its SHA-256 manifest.</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-slate-700">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
              <span>Re-verify chain of custody by matching the original WhatsApp export to the bundle's source hash.</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-slate-700">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
              <span>Runs 100% in the browser — evidence never leaves your machine.</span>
            </li>
          </ul>
          <RouterLink to="/#plans" data-testid="verify-paywall-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-700">
            <ShieldCheck size={17} /> View plans &amp; join the waitlist
          </RouterLink>
          <p className="mt-4 text-xs text-slate-400">
            Professional &amp; Enterprise subscriptions are launching soon — waitlist members get early access.
          </p>
        </div>
      </LegalPage>
    );
  }

  return (
    <LegalPage
      title="Verify Court Bundle | AuditPDF"
      description="Independently verify the integrity of an AuditPDF evidence bundle and its source chain of custody."
      canonical="https://auditpdfhq.com/verify">
      <div className="mx-auto max-w-5xl px-4 py-12">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-slate-900 text-white">
                <ShieldCheck size={22} />
            </span>
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Verification Dashboard</h1>
                <p className="text-sm text-slate-500">Confirm media integrity and chain of custody mathematically.</p>
            </div>
            </div>
            {(bundle || source) && (
                <button onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                    <RotateCcw size={16} /> Reset Dashboard
                </button>
            )}
        </div>

        <p className="mb-8 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          <Lock size={16} className="mt-0.5 shrink-0 text-slate-400" />
          <span>This check runs <strong>100% in your browser</strong>. Your files are never uploaded to any server.</span>
        </p>

        {error && (
          <div className="mb-8 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <XCircle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Master Match Banner (Only shows when BOTH are loaded) */}
        {bundle && source && (
            <div className={`mb-8 p-6 rounded-2xl border-2 shadow-sm flex flex-col md:flex-row items-center md:items-start gap-4 ${custodyMatch ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-400'}`}>
                {custodyMatch ? <CheckCircle2 size={48} className="text-emerald-600 shrink-0" /> : <AlertTriangle size={48} className="text-red-600 shrink-0" />}
                <div className="text-center md:text-left min-w-0 flex-1">
                    <h2 className={`text-2xl font-bold ${custodyMatch ? 'text-emerald-900' : 'text-red-900'}`}>
                        {custodyMatch ? "Chain of Custody is Verified" : "Chain of Custody FAILED"}
                    </h2>
                    <p className={`mt-1 text-sm ${custodyMatch ? 'text-emerald-800' : 'text-red-800'}`}>
                        {custodyMatch 
                            ? "The cryptographic hash of the original WhatsApp export perfectly matches the source hash recorded inside the AuditPDF bundle. This PDF is a true and unaltered rendering of the source data."
                            : "The hash of the uploaded WhatsApp export does NOT match the source hash recorded by AuditPDF. This source file was not used to generate this bundle."}
                    </p>
                </div>
            </div>
        )}

        {/* Dual Slot Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
            
            {/* SLOT A: AUDITPDF BUNDLE */}
            <div className="flex flex-col border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-sm">
                <div className="bg-slate-50 border-b border-slate-200 p-4 flex items-center gap-2">
                    <FileArchive size={18} className="text-slate-500" />
                    <h3 className="font-semibold text-slate-800">1. AuditPDF Bundle</h3>
                </div>
                
                <div className="p-6 flex-1 flex flex-col">
                    {!bundle ? (
                        <div
                            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files, "bundle"); }}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={() => !busy && bundleInputRef.current?.click()}
                            className="flex-1 cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-xl p-8 text-center transition-colors min-h-[250px]"
                        >
                            <input ref={bundleInputRef} type="file" accept=".zip" className="hidden" onChange={(e) => processFiles(e.target.files, "bundle")} />
                            {busy ? <Loader2 size={32} className="animate-spin text-slate-400 mb-4" /> : <UploadCloud size={40} className="text-[#2A9D8F] mb-4" />}
                            <p className="font-semibold text-slate-700 mb-1">Upload Generated Bundle</p>
                            <p className="text-xs text-slate-500">Drop the .zip file produced by AuditPDF</p>
                        </div>
                    ) : (
                        <div className="animate-in fade-in slide-in-from-bottom-4">
                            <div className="mb-6 flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">File Name</p>
                                    <p className="text-sm font-medium text-slate-900 truncate">{bundle.fileName}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ledger Date (UTC)</p>
                                    <p className="text-sm font-mono text-slate-700">{bundle.generatedAt}</p>
                                </div>
                            </div>

                            <div className="mb-6">
                                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Recorded Source Hash</p>
                                {/* DYNAMIC COLOR APPLIED HERE */}
                                <p className={`text-sm font-mono p-3 rounded-lg border break-all select-all transition-colors ${hashBoxStyle}`}>
                                    {bundle.sourceHash}
                                </p>
                            </div>

                            <div className={`flex items-center gap-3 p-4 rounded-xl border ${bundlePassed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                                {bundlePassed ? <CheckCircle2 size={24} className="text-emerald-600 shrink-0" /> : <XCircle size={24} className="text-red-600 shrink-0" />}
                                <div>
                                    <p className={`text-sm font-bold ${bundlePassed ? 'text-emerald-800' : 'text-red-800'}`}>
                                        {bundlePassed ? "Media Ledger Verified" : "Media Ledger Failed"}
                                    </p>
                                    <p className={`text-xs ${bundlePassed ? 'text-emerald-700' : 'text-red-700'}`}>
                                        {okCount} of {bundle.rows.length} files intact.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* SLOT B: SOURCE EXPORT */}
            <div className="flex flex-col border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-sm">
                <div className="bg-slate-50 border-b border-slate-200 p-4 flex items-center gap-2">
                    <Link size={18} className="text-slate-500" />
                    <h3 className="font-semibold text-slate-800">2. Original WhatsApp Export</h3>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                    {!source ? (
                        <div
                            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files, "source"); }}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={() => !busy && sourceInputRef.current?.click()}
                            className="flex-1 cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-xl p-8 text-center transition-colors min-h-[250px]"
                        >
                            <input ref={sourceInputRef} type="file" accept=".zip,.txt" className="hidden" onChange={(e) => processFiles(e.target.files, "source")} />
                            {busy ? <Loader2 size={32} className="animate-spin text-slate-400 mb-4" /> : <UploadCloud size={40} className="text-indigo-500 mb-4" />}
                            <p className="font-semibold text-slate-700 mb-1">Upload Source Evidence</p>
                            <p className="text-xs text-slate-500">Drop the raw .zip or .txt from the custodian</p>
                        </div>
                    ) : (
                        <div className="animate-in fade-in slide-in-from-bottom-4 flex-1 flex flex-col">
                             <div className="mb-6 flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Extracted Source File</p>
                                    <p className="text-sm font-medium text-slate-900 truncate">{source.fileName}</p>
                                </div>
                            </div>

                            <div className="mb-6 flex-1">
                                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Computed SHA-256 Hash</p>
                                {/* DYNAMIC COLOR APPLIED HERE */}
                                <p className={`text-sm font-mono p-3 rounded-lg border break-all select-all transition-colors ${hashBoxStyle}`}>
                                    {source.hash}
                                </p>
                                
                                {!bundle && (
                                    <p className="mt-4 text-sm text-slate-500">
                                        This is the mathematical fingerprint of the source text. <strong>Upload the AuditPDF bundle to the left</strong> to verify the chain of custody automatically.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </div>
        
        {/* Full Media Ledger Table (Only shows if Bundle is uploaded and expanded) */}
        {bundle && (
             <div className="mt-8 border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-sm">
                <div className="bg-slate-50 border-b border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-800">Media Integrity Ledger Detail</h3>
                </div>
                <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {bundle.rows.map((r) => {
                    const s = STATUS[r.status];
                    const Icon = s.icon;
                    return (
                    <li key={r.rel} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 px-6 py-3 hover:bg-slate-50">
                        <div className="flex min-w-0 items-center gap-3">
                        <Icon size={18} className={`shrink-0 ${s.cls}`} />
                        <span className="truncate font-mono text-xs sm:text-sm text-slate-700" title={r.rel}>{r.rel}</span>
                        </div>
                        <span className={`self-start sm:self-center shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.chip}`}>
                        {s.label}
                        </span>
                    </li>
                    );
                })}
                </ul>
            </div>
        )}
      </div>
    </LegalPage>
  );
}